import type { CSSProperties } from "react";
import { Icon } from "@/components/ui/Icon";
import { t } from "@/lib/strings";
import type { FocusUnit } from "@/lib/memoFocusUnits";

// Slim "where am I" bar at the top of the presentation overlay. Shows
// Abschnitt > Zwischenabschnitt, the position, and a thin progress line. In
// `free` mode the caller passes the unit nearest the viewport centre, so it
// stays oriented while the reader scrolls with the mouse.
export function MemoPresentBar({
  units,
  index,
  locked = false,
  mayEditHistory = false,
  unlocked = false,
  onToggleUnlock,
}: {
  units: FocusUnit[];
  index: number | null;
  /** A unit is focused — only then may history be unlocked. */
  locked?: boolean;
  /** Only series editors / workspace-admins / flow-admins may unlock history. */
  mayEditHistory?: boolean;
  unlocked?: boolean;
  onToggleUnlock?: () => void;
}) {
  if (index === null || units.length === 0) return null;
  const unit = units[index];
  if (!unit) return null;
  const pct = Math.round(((index + 1) / units.length) * 100);

  return (
    <div style={{ ...bar, ...(unlocked ? unlockedBar : null) }}>
      <div style={row}>
        <span style={crumb}>
          <span style={blockTitle}>{unit.blockTitle}</span>
          {unit.subheadingTitle ? (
            <>
              <span style={sep}>›</span>
              <span style={subTitle}>{unit.subheadingTitle}</span>
            </>
          ) : null}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          {/* Editing an earlier meeting's record is deliberate, and visible: the
              presenter is screen-sharing, so the room sees the bar change state. */}
          {locked && mayEditHistory ? (
            <button type="button" onClick={onToggleUnlock} style={unlockBtn}>
              <Icon name="lock" size={12} color={unlocked ? "var(--warn)" : "var(--ink-3)"} />
              {unlocked ? t.memoPresentUnlocked : t.memoPresentUnlock}
            </button>
          ) : null}
          <span style={count}>
            {index + 1} / {units.length}
          </span>
        </span>
      </div>
      <div style={track}>
        <div data-progress-fill style={{ ...fill, width: `${pct}%` }} />
      </div>
    </div>
  );
}

const bar: CSSProperties = {
  flexShrink: 0,
  background: "var(--surface-elev)",
  borderBottom: "1px solid var(--hairline)",
};

const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  padding: "8px 18px",
  fontSize: 13,
};

const crumb: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  minWidth: 0,
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
};

const blockTitle: CSSProperties = { color: "var(--ink-2)", fontWeight: 600 };
const sep: CSSProperties = { color: "var(--ink-4)" };
const subTitle: CSSProperties = { color: "var(--ink-1)", fontWeight: 650 };
const count: CSSProperties = { color: "var(--ink-3)", fontWeight: 600, flexShrink: 0 };

// Both grounds flip with the theme (no hardcoded #fff/#000 against a var token).
const unlockedBar: CSSProperties = {
  background: "color-mix(in srgb, var(--warn) 12%, var(--surface-elev))",
};

const unlockBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "2px 4px",
  fontSize: 12,
  fontWeight: 600,
  color: "var(--ink-2)",
};

const track: CSSProperties = { height: 2, background: "var(--hairline)" };
const fill: CSSProperties = { height: 2, background: "var(--ink-2)", transition: "width .15s" };
